import { useRef, useEffect, useState } from 'react'
import { Bold, Italic, Underline, List, ListOrdered, Link } from 'lucide-react'

interface RichNoteEditorProps {
    value: string
    onChange: (html: string) => void
    minHeight?: number
    autoFocus?: boolean
}

export const RichNoteEditor = ({ value, onChange, minHeight = 120, autoFocus = false }: RichNoteEditorProps) => {
    const editorRef = useRef<HTMLDivElement>(null)
    const [formatState, setFormatState] = useState({ bold: false, italic: false, underline: false })

    useEffect(() => {
        if (editorRef.current && editorRef.current.innerHTML !== value) {
            editorRef.current.innerHTML = value
        }
    }, [])

    useEffect(() => {
        if (autoFocus) editorRef.current?.focus()
    }, [autoFocus])

    const updateFormatState = () => {
        const sel = window.getSelection()
        if (!sel || !sel.rangeCount) return
        let isBold = false, isItalic = false, isUnderline = false
        let node: Node | null = sel.getRangeAt(0).commonAncestorContainer
        while (node && node !== editorRef.current) {
            const tag = (node as Element).tagName
            if (tag === 'STRONG' || tag === 'B') isBold = true
            if (tag === 'EM' || tag === 'I') isItalic = true
            if (tag === 'U') isUnderline = true
            node = node.parentNode
        }
        setFormatState({ bold: isBold, italic: isItalic, underline: isUnderline })
    }

    const exec = (command: string, value?: string) => {
        editorRef.current?.focus()
        document.execCommand(command, false, value)
        setTimeout(updateFormatState, 0)
        onChange(editorRef.current?.innerHTML ?? '')
    }

    const insertLink = () => {
        const url = prompt("Entrez l'URL du lien:")
        if (url) exec('createLink', url)
    }

    const btnClass = (active?: boolean) =>
        `p-1.5 rounded hover:bg-gray-200 transition-colors ${active ? 'bg-blue-100 text-blue-700' : 'text-gray-600'}`

    return (
        <div className="border border-blue-400 rounded-md overflow-hidden focus-within:ring-2 focus-within:ring-blue-500">
            {/* Toolbar */}
            <div className="flex items-center gap-0.5 px-2 py-1 bg-gray-50 border-b border-gray-200">
                <button type="button" onMouseDown={e => { e.preventDefault(); exec('bold') }} className={btnClass(formatState.bold)} title="Gras"><Bold size={14} /></button>
                <button type="button" onMouseDown={e => { e.preventDefault(); exec('italic') }} className={btnClass(formatState.italic)} title="Italique"><Italic size={14} /></button>
                <button type="button" onMouseDown={e => { e.preventDefault(); exec('underline') }} className={btnClass(formatState.underline)} title="Souligné"><Underline size={14} /></button>
                <div className="w-px bg-gray-300 mx-1 h-4" />
                <button type="button" onMouseDown={e => { e.preventDefault(); exec('insertUnorderedList') }} className={btnClass()} title="Liste à puces"><List size={14} /></button>
                <button type="button" onMouseDown={e => { e.preventDefault(); exec('insertOrderedList') }} className={btnClass()} title="Liste numérotée"><ListOrdered size={14} /></button>
                <div className="w-px bg-gray-300 mx-1 h-4" />
                <button type="button" onMouseDown={e => { e.preventDefault(); insertLink() }} className={btnClass()} title="Lien"><Link size={14} /></button>
            </div>

            {/* Editor */}
            <div
                ref={editorRef}
                contentEditable
                suppressContentEditableWarning
                className="w-full px-3 py-2 bg-white focus:outline-none text-sm text-gray-800"
                style={{ minHeight, lineHeight: '1.6' }}
                onInput={() => onChange(editorRef.current?.innerHTML ?? '')}
                onMouseUp={updateFormatState}
                onKeyUp={updateFormatState}
            />
        </div>
    )
}
